import { Fragment } from 'react/jsx-runtime';
import { Fleet } from '../../types/Fleet';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../common/dropdown';
import FleetsService from '../../services/fleets.services';
import { toastError, toastSuccess } from '../../App';
import { Device } from '../../types/Device';
import DeviceTable from './DeviceTable';

export type Props = {
  open: boolean;
  rows: Fleet[];
  onSave: () => {};
  setOpen: (open: boolean) => void;
  selectedFleetId?: string | null;
};

export default function FleetModal(props: Props) {
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [devices, setDevices] = useState<Device[]>();
  const [name, setName] = useState('');
  const [type, setType] = useState<any>();
  const cancelButtonRef = useRef(null);
  const isUpdate = !!props.selectedFleetId;
  const { t } = useTranslation();
  const canSave = true;

  const types: any[] = [
    { value: 'summit-compressor', label: t('device.summitCompressor') },
    // { value: 'bloom', label: t('device.bloom') },
  ];

  const handleSave = async () => {
    const isUpdating = isUpdate && props.selectedFleetId;

    const fleet = isUpdating ? createUpdatedFleet() : createNewFleet();

    try {
      if (isUpdating && props.selectedFleetId) {
        await FleetsService.updateFleet(props.selectedFleetId, fleet);
      } else {
        await FleetsService.addFleet(fleet);
      }
      props.onSave();
    } catch (error) {
      console.error('Error saving Fleet:', error);
      toastError(t('fleets.saveError'));
    } finally {
      props.setOpen(false);
      toastSuccess(t('fleets.saved'));
    }
  };

  const createUpdatedFleet = () => {
    return {
      ...(fleet?.name !== name && { name }),
      ...(fleet?.type !== type.value && { type: type.value }),
    };
  };

  const createNewFleet = () => ({
    name,
    type: type.value,
    logic: {},
  });

  const fetchFleetDevices = async () => {
    if (props.selectedFleetId) {
        const devicesResponse = await FleetsService.getDevicesInFleet(props.selectedFleetId)
        const devicesData = await devicesResponse.json();
        setDevices(devicesData);
    }
  };

  useEffect(() => {
    if (props.selectedFleetId) {
      fetchFleetDevices();

      FleetsService.getFleet(props.selectedFleetId).then((response) => {
        response.json().then((data) => {
          setFleet(data);
          setName(data.name);
          setType(types.find((option) => option.value === data.type));
        });
      });
    }
    else {
      setFleet(null);
      setName("");
      setType(null);
    }
  }, [props.selectedFleetId]);

  return (
    <>
      <Transition.Root show={props.open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={props.setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="mt-24 relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div>
                    <div className="flex justify-between">
                      <h2
                        id="payment-details-heading"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {t('fleets.fleets')}
                      </h2>

                      <button
                        type="button"
                        className="flex rounded-md p-1 border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        ref={cancelButtonRef}
                        onClick={() => props.setOpen(false)}
                      >
                        <XMarkIcon
                          className="h-7 w-7"
                          aria-hidden="true"
                        ></XMarkIcon>
                      </button>
                    </div>

                    <div className="mt-6 grid grid-cols-4 gap-6">
                      <div className="col-span-4 sm:col-span-2">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('fleets.name')}
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="off"
                          className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="block text-sm font-medium text-gray-700">
                        <label
                          htmlFor="type"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('fleets.type')}
                        </label>

                        <Dropdown
                          options={types}
                          selectedOption={type}
                          onChange={setType}
                          disabled={isUpdate}
                        />
                      </div>
                    </div>
                    { devices && <DeviceTable devices={devices} /> }
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-1 sm:gap-3">
                      <button
                        type="button"
                        className={`inline-flex w-full justify-center rounded-md border border-transparent ${canSave
                            ? 'bg-indigo-600 hover:bg-indigo-700'
                            : 'bg-indigo-200'
                          } px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm`}
                        disabled={!canSave}
                        onClick={handleSave}
                      >
                        {t('save')}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
